/**
 * useSocket Hook - Fan Frontend
 * Manages WebSocket connection with auto-reconnect and state management
 *
 * Features:
 * - Automatic connection on mount
 * - Auto-reconnect with exponential backoff
 * - Connection state tracking
 * - Error handling
 * - Cleanup on unmount
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { io, Socket } from 'socket.io-client'

interface UseSocketOptions {
  autoConnect?: boolean
  reconnectionAttempts?: number
  reconnectionDelay?: number
}

interface UseSocketReturn {
  socket: Socket | null
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  connect: () => void
  disconnect: () => void
}

/**
 * Custom hook for managing WebSocket connection
 * @param url - WebSocket server URL (e.g., 'http://localhost:3000')
 * @param token - JWT authentication token
 * @param options - Connection options
 */
export function useSocket(
  url: string,
  token: string | null,
  options: UseSocketOptions = {}
): UseSocketReturn {
  const {
    autoConnect = true,
    reconnectionAttempts = 5,
    reconnectionDelay = 1000
  } = options

  const socketRef = useRef<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const connect = useCallback(() => {
    if (!token) {
      setError('No authentication token')
      return
    }

    // Check if already connecting or connected
    if (socketRef.current?.connected) {
      return
    }

    const socketState = socketRef.current as any
    if (socketState?.connecting) {
      return
    }

    // Clean up any existing socket before creating new one
    if (socketRef.current) {
      socketRef.current.removeAllListeners()
      socketRef.current.disconnect()
      socketRef.current = null
    }

    setIsConnecting(true)
    setError(null)

    // Create socket connection
    const socket = io(url, {
      auth: {
        token: token // Ensure token is passed in auth object
      },
      transports: ['polling', 'websocket'], // START with polling for reliability
      reconnection: true,
      reconnectionAttempts,
      reconnectionDelay,
      reconnectionDelayMax: 5000,
      timeout: 60000, // Increased timeout to 60 seconds
      autoConnect: false, // We'll connect manually
      forceNew: true // Force new connection
    })

    socketRef.current = socket

    // CONNECTION EVENTS
    socket.on('connect', () => {
      setIsConnected(true)
      setIsConnecting(false)
      setError(null)
    })

    socket.on('disconnect', (reason) => {
      setIsConnected(false)
      setIsConnecting(false)

      // Auto-reconnect unless it was a manual disconnect
      if (reason === 'io server disconnect') {
        // Server disconnected us, try to reconnect
        socket.connect()
      }
    })

    socket.on('connect_error', async (err) => {
      console.error('[useSocket] ❌ CONNECTION ERROR:', {
        message: err.message,
        type: (err as any).type,
        data: (err as any).data
      })

      // Log the full error for debugging
      console.error('[useSocket] Full error:', err)

      setIsConnecting(false)
      setError(err.message)

      // If authentication failed, try to refresh token before giving up
      if (err.message && (err.message.includes('Authentication') || err.message.includes('token') || err.message.includes('expired'))) {
        const currentToken = localStorage.getItem('auth_token')
        if (currentToken) {
          try {
            const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
            const refreshResponse = await fetch(`${API_URL}/api/auth/refresh-token`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${currentToken}`,
                'Content-Type': 'application/json'
              }
            })

            if (refreshResponse.ok) {
              const data = await refreshResponse.json()
              localStorage.setItem('auth_token', data.token)
              if (data.user) {
                localStorage.setItem('user_data', JSON.stringify(data.user))
              }

              // Update socket auth with new token and reconnect
              socket.auth = { token: data.token }
              socket.connect()
              return // Don't disconnect
            } else {
              console.error('[useSocket] Token refresh failed, disconnecting')
            }
          } catch (refreshErr) {
            console.error('[useSocket] Token refresh request failed:', refreshErr)
          }
        }

        // Refresh failed or no token - disconnect
        console.error('[useSocket] Authentication failed, stopping reconnection attempts')
        socket.disconnect()
      }
    })

    socket.on('error', (err) => {
      console.error('[useSocket] Socket error:', err)
      setError(typeof err === 'string' ? err : 'Socket error occurred')
    })

    // Add a connection timeout handler
    const connectionTimeout = setTimeout(() => {
      if (!socket.connected) {
        console.error('[useSocket] ⏱️ Connection timeout - WebSocket failed to connect within 10 seconds')
        setError('Connection timeout')
      }
    }, 10000)

    socket.on('connect', () => {
      clearTimeout(connectionTimeout)
    })

    // RECONNECTION EVENTS
    socket.io.on('reconnect_attempt', () => {
      setIsConnecting(true)
    })

    socket.io.on('reconnect', () => {
      setIsConnected(true)
      setIsConnecting(false)
      setError(null)
    })

    socket.io.on('reconnect_failed', () => {
      console.error('[useSocket] Reconnection failed after all attempts')
      setIsConnecting(false)
      setError('Failed to reconnect to server')
    })

    // Add transport-level debugging
    socket.io.on('open', () => {
      // no-op
    })

    socket.io.on('error', (error) => {
      console.error('[useSocket] 🔴 Transport error:', error)
    })

    socket.io.on('close', () => {
      // no-op
    })

    // Connect
    socket.connect()

    // Check connection state after a short delay
    setTimeout(() => {
      // If still not connected after 1s, check for errors
      if (!socket.connected && !(socket as any).connecting) {
        console.error('[useSocket] 🔴 Connection failed silently! Forcing reconnect...')
        socket.io.open()
      }
    }, 1000)
  }, [url, token, reconnectionAttempts, reconnectionDelay])

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      socketRef.current = null
      setIsConnected(false)
      setIsConnecting(false)
    }
  }, [])

  // Track if mounted
  const isMountedRef = useRef(true)

  // Auto-connect on mount if enabled
  useEffect(() => {
    isMountedRef.current = true

    if (autoConnect && token) {
      // Connect immediately if we have a token
      if (!socketRef.current?.connected) {
        connect()
      }
    }

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false
      // Don't disconnect in development due to StrictMode
      if (process.env.NODE_ENV === 'production' && socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, []) // Remove dependencies to run only once

  return {
    socket: socketRef.current,
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect
  }
}
